import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

/* A ratio rather than 1, because an item's width against the track's is rarely a
   whole number of pixels and a strict 1 makes the arrows flicker as the browser
   rounds. */
const FULLY_VISIBLE_RATIO = 0.99;

export interface EdgeVisibility {
  isAtEnd: boolean;
  isAtStart: boolean;
}

/**
 * Reports whether the track's first and last items are fully in view, which is
 * what enables and disables the arrows.
 *
 * Observing the two edge items is the whole reason the arrows are testable.
 * Deriving the same answer from `scrollWidth`, `scrollLeft` and `offsetWidth`,
 * as the Ribbons carousel does, needs all three stubbed on `HTMLElement.prototype`
 * before a test can reach the arrows at all, because jsdom has no layout. An
 * intersection is something a test can simply state.
 *
 * Both default to `true`, so a carousel whose items already fit renders with both
 * arrows disabled and stays that way, rather than showing them enabled for a
 * frame before the first observation arrives.
 */
export const useEdgeVisibility = (
  trackRef: RefObject<HTMLElement>,
  itemCount: number,
): EdgeVisibility => {
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(true);

  useEffect(() => {
    const track = trackRef.current;
    const first = track?.firstElementChild;
    const last = track?.lastElementChild;

    if (!track || !first || !last) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isFullyVisible = entry.intersectionRatio >= FULLY_VISIBLE_RATIO;

          if (entry.target === first) {
            setIsAtStart(isFullyVisible);
          }

          if (entry.target === last) {
            setIsAtEnd(isFullyVisible);
          }
        });
      },
      // The track is the scroll container, so it is also what "in view" is
      // measured against. The page's own scroll position is irrelevant here.
      { root: track, threshold: FULLY_VISIBLE_RATIO },
    );

    observer.observe(first);
    observer.observe(last);

    return () => observer.disconnect();
    // Re-observes when the set changes size, since the edge elements are then
    // different nodes.
  }, [itemCount, trackRef]);

  return { isAtEnd, isAtStart };
};
